/**
 * ProtocolCard - Tarjeta individual de protocolo
 */

import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, Info, ChevronDown, ChevronUp, Flame, Check } from 'lucide-react';
import { useUIStore } from '@/store';
import { cn, formatMinutes, calculateProtocolProgress } from '@/core/utils';
import { getLayerConfig } from '@/core/constants';
import { feedbackComplete } from '@/services/feedback';
import type { Protocol } from '@/core/types';
import { ProgressBar, Badge, Card } from '@/ui/primitives';
import { SubtaskItem } from './SubtaskItem';

interface ProtocolCardProps {
  protocol: Protocol;
  showIdealTime?: boolean;
}

export function ProtocolCard({ protocol, showIdealTime }: ProtocolCardProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showRationale, setShowRationale] = useState(false);
  const [justCompleted, setJustCompleted] = useState(false);
  const prevCompletedRef = useRef(protocol.isCompleted);
  const preferences = useUIStore((state) => state.preferences);
  const config = getLayerConfig(protocol.layer);

  const progress = calculateProtocolProgress(protocol);
  const completedCount = protocol.subtasks.filter((s) => s.isDone).length;

  // Detectar cuando el protocolo se acaba de completar
  useEffect(() => {
    if (protocol.isCompleted && !prevCompletedRef.current && preferences.animationsEnabled) {
      setJustCompleted(true);
      // Feedback haptico y sonoro de celebracion
      feedbackComplete();
      const timer = setTimeout(() => setJustCompleted(false), 1500);
      prevCompletedRef.current = protocol.isCompleted;
      return () => clearTimeout(timer);
    }
    prevCompletedRef.current = protocol.isCompleted;
    return undefined;
  }, [protocol.isCompleted, preferences.animationsEnabled]);

  return (
    <motion.div
      animate={justCompleted ? { scale: [1, 1.02, 1] } : {}}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className="relative"
    >
      <Card
        variant={protocol.layer}
        padding="none"
        className={cn(
          'transition-all duration-300 overflow-hidden',
          protocol.isCompleted && 'opacity-60'
        )}
      >
        {/* Animación de celebración al completar */}
        <AnimatePresence>
          {justCompleted && (
            <motion.div
              className="absolute inset-0 pointer-events-none z-10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <motion.div
                className={cn('absolute inset-0', config.color.bgTransparent)}
                initial={{ opacity: 0 }}
                animate={{ opacity: [0, 0.3, 0] }}
                transition={{ duration: 1 }}
              />
              <motion.div
                className="absolute top-4 right-4"
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: 'spring', damping: 10, stiffness: 200 }}
              >
                <div className={cn('p-1.5 rounded-full', config.color.bg)}>
                  <Check className="w-4 h-4 text-white" strokeWidth={3} />
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Header */}
        <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={cn(
          'w-full flex items-start justify-between p-4 text-left',
          'transition-colors hover:bg-slate-800/30'
        )}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {protocol.emoji && (
              <span className="text-lg" role="img" aria-hidden="true">
                {protocol.emoji}
              </span>
            )}
            <h3
              className={cn(
                'font-medium text-slate-100 truncate',
                protocol.isCompleted && 'line-through text-slate-500'
              )}
            >
              {protocol.title}
            </h3>
            {protocol.streak > 0 && (
              <div className="flex items-center gap-1 text-amber-500">
                <Flame className="w-3 h-3" />
                <span className="text-xs font-medium">{protocol.streak}</span>
              </div>
            )}
          </div>

          {protocol.description && (
            <p className="text-xs text-slate-500 truncate">
              {protocol.description}
            </p>
          )}

          {/* Meta info */}
          <div className="flex items-center gap-3 mt-2">
            {showIdealTime && protocol.idealTime && (
              <div className="flex items-center gap-1 text-slate-400">
                <Clock className="w-3 h-3" />
                <span className="text-xs font-medium">{protocol.idealTime}</span>
              </div>
            )}
            <div className="flex items-center gap-1 text-slate-500">
              <Clock className="w-3 h-3" />
              <span className="text-xs">{formatMinutes(protocol.timeEstimate)}</span>
            </div>

            <Badge variant="subtle" size="xs">
              {completedCount}/{protocol.subtasks.length}
            </Badge>

            {preferences.showRationale && (
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowRationale(!showRationale);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.stopPropagation();
                    setShowRationale(!showRationale);
                  }
                }}
                className="p-1 rounded hover:bg-slate-700 transition-colors cursor-pointer"
                aria-label="Ver fundamento"
              >
                <Info className="w-3 h-3 text-slate-500" />
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 ml-4">
          <div className="w-16">
            <ProgressBar value={progress} layer={protocol.layer} size="sm" />
          </div>
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-slate-500" />
          ) : (
            <ChevronDown className="w-4 h-4 text-slate-500" />
          )}
        </div>
      </button>

      {/* Rationale */}
      <AnimatePresence>
        {showRationale && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div
              className={cn(
                'mx-4 mb-4 p-3 rounded-lg text-xs',
                config.color.bgTransparent,
                config.color.text
              )}
            >
              <p className="font-medium mb-1">Por qué es importante:</p>
              <p className="opacity-80">{protocol.rationale}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Subtasks */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 space-y-1">
              {protocol.subtasks.map((subtask, index) => (
                <SubtaskItem
                  key={subtask.id}
                  subtask={subtask}
                  protocolId={protocol.id}
                  layer={protocol.layer}
                  index={index}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      </Card>
    </motion.div>
  );
}
